export type ImageEngineId = 'draft' | 'standard' | 'pro';
export type EditModeId = 'basic' | 'advanced' | '4k';
export type ProviderType = 'stability' | 'openai' | 'nanobanana' | 'leonardo';

export interface ImageEngineConfig {
  id: ImageEngineId;
  label: string;
  tagline: string;
  description: string;
  credits: number; // single-image cost (kept for display/reference)
  provider: ProviderType;
  recommendedUse: string;
  badge?: 'recommended' | 'best-quality';
  icon: string;
}

export interface EditModeConfig {
  id: EditModeId;
  label: string;
  description: string;
  credits: number;
  provider: ProviderType;
  recommendedUse: string;
  icon: string;
}

export const IMAGE_ENGINES: ImageEngineConfig[] = [
  {
    id: 'draft',
    label: 'Draft Mode',
    tagline: 'Fastest, best for concept exploration',
    description: 'Quick iterations with Leonardo AI. Great for testing concepts before committing credits.',
    credits: 5,
    provider: 'leonardo',
    recommendedUse: 'Concept testing & rapid iteration',
    icon: 'ri-draft-line',
  },
  {
    id: 'standard',
    label: 'Standard Mode',
    tagline: 'Best balance of quality and speed',
    description: 'OpenAI image generation delivers sharp, professional-grade ad creatives at great speed.',
    credits: 10,
    provider: 'openai',
    recommendedUse: 'Most campaigns and everyday use',
    badge: 'recommended',
    icon: 'ri-image-line',
  },
  {
    id: 'pro',
    label: 'Pro Creative Mode',
    tagline: 'Premium text rendering & advanced layouts',
    description: 'Nano Banana Pro engine: highest quality output, complex compositions, and superior in-image text.',
    credits: 25,
    provider: 'nanobanana',
    recommendedUse: 'Premium campaigns requiring maximum impact',
    badge: 'best-quality',
    icon: 'ri-sparkling-2-line',
  },
];

export const EDIT_MODES: EditModeConfig[] = [
  {
    id: 'basic',
    label: 'Basic Edit',
    description: 'Background replacement, lighting changes, minor adjustments',
    credits: 10,
    provider: 'openai',
    recommendedUse: 'Quick tweaks and simple changes',
    icon: 'ri-edit-line',
  },
  {
    id: 'advanced',
    label: 'Advanced Pro Edit',
    description: 'Complex transformations, layout changes, multi-element edits, text inside images',
    credits: 20,
    provider: 'nanobanana',
    recommendedUse: 'Significant reworks and complex changes',
    icon: 'ri-settings-4-line',
  },
  {
    id: '4k',
    label: '4K Pro Creative',
    description: 'High-resolution output, premium ad creatives, detailed compositions',
    credits: 40,
    provider: 'nanobanana',
    recommendedUse: 'Top-tier campaigns requiring max resolution',
    icon: 'ri-hd-line',
  },
];

// ─── Bulk Credit Pricing Table ────────────────────────────────────────────────
// [engine][imageCount] = credit cost
// Draft (cheap, high usage):   1→5   3→12  6→21
// Standard (core product):     1→10  3→27  6→48
// Pro (premium, controlled):   1→25  3→69  6→126

export const BULK_CREDIT_TABLE: Record<ImageEngineId, Record<1 | 3 | 6, number>> = {
  draft:    { 1: 5,  3: 12, 6: 21  },
  standard: { 1: 10, 3: 27, 6: 48  },
  pro:      { 1: 25, 3: 69, 6: 126 },
};

export function getBulkCreditCost(engine: ImageEngineId, count: 1 | 3 | 6): number {
  return BULK_CREDIT_TABLE[engine]?.[count] ?? BULK_CREDIT_TABLE['standard'][1];
}

// ─── Pro Bulk Guardrails ──────────────────────────────────────────────────────
// Applied to Agency users running 6x Pro Creative batches to prevent abuse.

export const PRO_BULK_GUARDRAILS = {
  /** Max 6x Pro Creative generations per calendar day (per user) */
  dailyCap: 3,
  /** Minimum minutes between consecutive 6x Pro Creative batches */
  cooldownMinutes: 10,
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getEngineConfig(id: ImageEngineId): ImageEngineConfig {
  return IMAGE_ENGINES.find((e) => e.id === id) ?? IMAGE_ENGINES[1];
}

export function getEditModeConfig(id: EditModeId): EditModeConfig {
  return EDIT_MODES.find((e) => e.id === id) ?? EDIT_MODES[0];
}
